import { Bell, Search } from 'lucide-react'
import * as React from 'react'
import { cn } from '../../lib/cn'
import { Avatar } from '../atoms/Avatar'
import { Badge } from '../atoms/Badge'
import { Button } from '../atoms/Button'
import { Icon } from '../atoms/Icon'
import { Kbd } from '../atoms/Kbd'

/**
 * TopHeader — sits above main content in AppShell.
 * Pencil: bAD8f (Dark) / IjgvO (Light) / EQpTT (base).
 *
 * Slots: breadcrumb/title (left), command palette trigger (center),
 * notifications + user avatar (right).
 */
export interface TopHeaderProps extends Omit<React.HTMLAttributes<HTMLElement>, 'title'> {
  title?: React.ReactNode
  onOpenCommandPalette?: () => void
  onOpenNotifications?: () => void
  notificationCount?: number
  user?: { name: string; initials: string; avatarUrl?: string }
  onUserClick?: () => void
  rightSlot?: React.ReactNode
  /** Override the default user avatar button with a custom element (e.g., dropdown). */
  userSlot?: React.ReactNode
}

export const TopHeader = React.forwardRef<HTMLElement, TopHeaderProps>(
  (
    {
      className,
      title,
      onOpenCommandPalette,
      onOpenNotifications,
      notificationCount = 0,
      user,
      onUserClick,
      rightSlot,
      userSlot,
      ...props
    },
    ref,
  ) => (
    <header
      ref={ref}
      className={cn(
        'flex h-16 items-center gap-4 px-6 shrink-0 border-b border-ia-border bg-background/95 backdrop-blur-sm',
        className,
      )}
      {...props}
    >
      {title && (
        <div className="flex-1 min-w-0 text-body-md font-semibold text-ia-text truncate">
          {title}
        </div>
      )}
      {!title && <div className="flex-1" />}

      {/* Command palette trigger */}
      {onOpenCommandPalette && (
        <button
          type="button"
          onClick={onOpenCommandPalette}
          className="flex items-center gap-3 h-9 min-w-[280px] rounded-pill border border-ia-border bg-ia-input-bg pl-3 pr-1 text-body-sm text-ia-text-tertiary hover:border-ia-border-strong focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          <Icon icon={Search} size="sm" />
          <span className="flex-1 text-left">Search or jump to…</span>
          <span className="flex items-center gap-[2px]">
            <Kbd>⌘</Kbd>
            <Kbd>K</Kbd>
          </span>
        </button>
      )}

      {rightSlot}

      {/* Notifications */}
      {onOpenNotifications && (
        <div className="relative">
          <Button
            size="icon"
            variant="ghost"
            onClick={onOpenNotifications}
            aria-label="Notifications"
          >
            <Bell />
          </Button>
          {notificationCount > 0 && (
            <Badge
              variant="error"
              size="sm"
              className="absolute -top-1 -right-1 min-w-[18px] h-[18px] justify-center px-[5px] tabular-nums"
            >
              {notificationCount > 9 ? '9+' : notificationCount}
            </Badge>
          )}
        </div>
      )}

      {/* User avatar — custom slot overrides default */}
      {userSlot ??
        (user && (
          <button
            type="button"
            onClick={onUserClick}
            aria-label={user.name}
            className="rounded-pill focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            <Avatar size="sm" initials={user.initials} src={user.avatarUrl} />
          </button>
        ))}
    </header>
  ),
)
TopHeader.displayName = 'TopHeader'
